import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  NgZone,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, Subscription } from 'rxjs';
import { formatCPFOrCNPJ } from 'src/app/helpers/utils/cpf-cnpj.util';
import { PsaAuthorizationService } from 'src/app/services/auth/psa-authorization.service';
import {
  EmissionStatusCode,
  PsaEmissionUpdateStatus,
  PsaHarvest,
  PsaProjectEmission,
} from 'src/app/shared/interfaces/psa-project-emission';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { EmissionCreditService } from '../../../services/emission-credit.service';
import {
  EmissionDrawerFooterMode,
  EmissionStepTag,
  extractEmissionFromApiResponse,
  resolveEmissionFooterMode,
  resolveEmissionStepTags,
  shouldShowRejectionReason,
} from './emission-consult-details-drawer.utils';

@Component({
  standalone: false,
  selector: 'app-emission-consult-details-drawer',
  templateUrl: './emission-consult-details-drawer.component.html',
  styleUrls: ['./emission-consult-details-drawer.component.scss'],
})
export class EmissionConsultDetailsDrawerComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  @Input() emissionId: number | null = null;
  @Output() openChange = new EventEmitter<boolean>();
  @Output() emissionUpdated = new EventEmitter<void>();

  emission?: PsaProjectEmission;
  harvests: PsaHarvest[] = [];
  isLoading = false;
  loadError: string | null = null;
  stepTags: EmissionStepTag[] = [];
  footerMode: EmissionDrawerFooterMode = 'understand';
  showRejectionReason = false;
  projectName = '-';
  rmLink = '';

  showRejectModal = false;
  showApproveModal = false;
  showCloneModal = false;

  private hasAuditPermission = false;
  private hasRegisterPermission = false;
  private loadSubscription?: Subscription;

  constructor(
    private readonly emissionCreditService: EmissionCreditService,
    private readonly psaAuthorizationService: PsaAuthorizationService,
    private readonly b3ToastService: AppToastService,
    private readonly datePipe: DatePipe,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone,
  ) {}

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.releaseOpenDialogs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['open'] || changes['emissionId']) && this.open && this.emissionId != null) {
      this.loadEmissionDetails(this.emissionId);
    }

    if (changes['open'] && !this.open) {
      this.resetState();
    }
  }

  close(): void {
    this.loadSubscription?.unsubscribe();
    this.dismissAllModals();
    this.open = false;
    this.openChange.emit(false);
  }

  trackByStepLabel(_index: number, step: EmissionStepTag): string {
    return step.label;
  }

  exportToCSV(): void {
    if (!this.emission) {
      return;
    }

    const flattenedData = this.flattenData([this.emission]);
    if (!flattenedData.length) {
      return;
    }

    const headers = Object.keys(flattenedData[0]);
    const csvContent =
      '\ufeff'
      + headers.join(';') + '\n'
      + flattenedData.map(row => headers.map(field => this.formatCSVField(row[field])).join(';')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const filename = `PSA_Emissao_Credito_${this.emission.serialCode}.csv`;
    this.downloadBlob(blob, filename);
    this.b3ToastService.show({
      variant: 'success',
      label: 'Arquivo com detalhes exportado com sucesso!',
    });
  }

  openRejectAnalysisModal(): void {
    this.showRejectModal = true;
  }

  onRejectModalDismiss(): void {
    this.showRejectModal = false;
    this.releaseOpenDialogs();
  }

  onRejectConfirmed(comment: string): void {
    this.showRejectModal = false;
    this.updateEmissionStatus(EmissionStatusCode.REJECTED_BY_AUDITOR, comment, false);
  }

  openApproveConfirmation(): void {
    this.showApproveModal = true;
  }

  onApproveModalDismiss(): void {
    this.showApproveModal = false;
    this.releaseOpenDialogs();
  }

  confirmApproveEmission(): void {
    this.showApproveModal = false;
    this.updateEmissionStatus(EmissionStatusCode.PENDING_DEVELOPER_CONFIRMATION, '', true);
  }

  confirmDeveloperApproveReport(): void {
    this.updateEmissionStatus(EmissionStatusCode.REGISTERED_IN_B3, '', true);
  }

  openCloneConfirmation(): void {
    this.showCloneModal = true;
  }

  onCloneModalDismiss(): void {
    this.showCloneModal = false;
    this.releaseOpenDialogs();
  }

  confirmCloneEmission(): void {
    this.showCloneModal = false;
    const serializedObject = JSON.stringify(this.emission ?? '');
    sessionStorage.setItem('CLONE_EMISSION_OBJECT', serializedObject);
    this.close();
    this.router.navigate(['/registro-emissao-credito']);
  }

  private loadEmissionDetails(emissionId: number): void {
    this.loadSubscription?.unsubscribe();
    this.isLoading = true;
    this.loadError = null;
    this.emission = undefined;
    this.harvests = [];
    this.loadPermissions();
    this.cdr.markForCheck();

    this.loadSubscription = this.loadCreditEmission(emissionId).pipe(
      finalize(() => {
        this.ngZone.run(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      }),
    ).subscribe({
      next: (emission) => {
        this.ngZone.run(() => {
          if (!emission) {
            this.loadError = 'Não foi possível carregar os detalhes da emissão.';
            this.updateViewModel();
            this.cdr.detectChanges();
            return;
          }

          try {
            this.emission = this.parseEmissionLoad(emission);
            this.harvests = this.emission.harvests ?? [];
            this.loadError = null;
          } catch (error) {
            console.error('Erro ao processar detalhes da emissão.', error);
            this.emission = undefined;
            this.harvests = [];
            this.loadError = 'Erro ao processar os detalhes da emissão.';
          }

          this.updateViewModel();
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          console.error('Erro ao carregar emissão de crédito.', error);
          this.loadError = 'Não foi possível carregar os detalhes da emissão.';
          this.updateViewModel();
          this.cdr.detectChanges();
        });
      },
    });
  }

  private updateViewModel(): void {
    this.stepTags = resolveEmissionStepTags(this.emission?.status);
    this.footerMode = resolveEmissionFooterMode(this.emission?.status, {
      hasAudit: this.hasAuditPermission,
      hasRegister: this.hasRegisterPermission,
    });
    this.showRejectionReason = shouldShowRejectionReason(this.emission?.status);
    this.projectName = this.emission?.project?.name?.trim() || '-';
    this.rmLink = this.emission?.rmLink?.trim() ?? '';
  }

  private loadPermissions(): void {
    const psaFunctions = this.psaAuthorizationService.getFunctions();
    this.hasAuditPermission = psaFunctions.some(
      (f: { functionCode?: string }) => f.functionCode === 'CAR_PSA_AUDITA_PROJETO',
    );
    this.hasRegisterPermission = psaFunctions.some(
      (f: { functionCode?: string }) => f.functionCode === 'CAR_PSA_REGISTRA_PROJETO',
    );
  }

  private loadCreditEmission(emissionId: number): Observable<PsaProjectEmission | undefined> {
    return this.emissionCreditService.getEmissionById(emissionId).pipe(
      map(res => extractEmissionFromApiResponse(res)),
      catchError(error => {
        console.error('Erro ao carregar emissão de crédito. Stack error: ', error);
        return of(undefined);
      }),
    );
  }

  private parseEmissionLoad(emission: PsaProjectEmission): PsaProjectEmission {
    const parsed = this.emissionCreditService.parseEmissionStatus([emission])[0];

    parsed.harvests = (parsed.harvests ?? []).map(harvest => ({
      ...harvest,
      developerName: harvest.developer?.name,
      developerId: harvest.developer?.id,
    }));

    parsed.endDate = this.formatDateOrDefault(parsed.endDate, 'dd/MM/yyyy', '00/00/0000');
    parsed.startDate = this.formatDateOrDefault(parsed.startDate, 'dd/MM/yyyy', '00/00/0000');
    parsed.insertDate = this.formatDateOrDefault(parsed.insertDate, 'dd/MM/yyyy HH:mm', '00/00/0000 00:00');

    const cleanedValue = parsed.cpfCnpjCode ? parsed.cpfCnpjCode.replace(/[^a-zA-Z0-9]/g, '') : '';
    parsed.cpfCnpjCode = formatCPFOrCNPJ(cleanedValue);

    return parsed;
  }

  private formatDateOrDefault(
    value: string | undefined,
    format: string,
    fallback: string,
  ): string {
    if (value == null || value === '') {
      return fallback;
    }

    const parsedDate = new Date(value);
    if (isNaN(parsedDate.getTime())) {
      return value;
    }

    return this.datePipe.transform(parsedDate, format) || fallback;
  }

  private updateEmissionStatus(
    status: EmissionStatusCode,
    description: string,
    clearDescription: boolean,
  ): void {
    if (this.emissionId == null) {
      console.error('Id da Emissão inválido, não é possível atualizar o status.');
      return;
    }

    const data: PsaEmissionUpdateStatus = {
      status,
      description: clearDescription ? '' : description,
    };

    this.emissionCreditService.projectEmissionUpdateStatus(this.emissionId, data).subscribe({
      next: () => {
        this.b3ToastService.show({
          status: 'success',
          text: 'Status da emissão atualizado com sucesso.',
        });
        this.loadEmissionDetails(this.emissionId as number);
        this.emissionUpdated.emit();
      },
      error: (err) => {
        const apiError = this.extractApiError(err);
        console.error(`Erro ao atualizar o status da emissão para ${status}`, {
          httpStatus: apiError.status,
          code: apiError.code,
          message: apiError.message,
          details: apiError.details,
          raw: apiError.raw,
        });
        this.b3ToastService.show({
          status: 'warning',
          text: this.resolveEmissionStatusErrorMessage(err),
        });
      },
    });
  }

  private extractApiError(err: unknown): {
    status: number | null;
    code: string | null;
    message: string | null;
    details: string | null;
    raw: unknown;
  } {
    if (!(err instanceof HttpErrorResponse)) {
      return { status: null, code: null, message: null, details: null, raw: err };
    }

    const payload = err.error;
    const firstError = Array.isArray(payload) && payload.length > 0
      ? payload[0]
      : payload;

    if (firstError && typeof firstError === 'object') {
      const errorObject = firstError as { code?: string; message?: string; details?: string };
      return {
        status: err.status,
        code: errorObject.code ?? null,
        message: errorObject.message ?? null,
        details: errorObject.details ?? null,
        raw: payload,
      };
    }

    return {
      status: err.status,
      code: null,
      message: typeof payload === 'string' ? payload : err.statusText,
      details: null,
      raw: payload,
    };
  }

  private resolveEmissionStatusErrorMessage(err: unknown): string {
    const defaultMessage = 'Não foi possível atualizar o status da emissão. Tente novamente ou contate o suporte.';
    const apiError = this.extractApiError(err);

    if (apiError.code === 'CertifierCreditEntityNotFound') {
      return 'Certificadora PSA Carbon Agro Perene não encontrada no banco deste ambiente. Confirme o INSERT em DEV e use npm run start:dev.';
    }

    if (apiError.code === 'InvalidStatusTransition') {
      return 'Transição de status não permitida. A emissão precisa estar aguardando confirmação do desenvolvedor.';
    }

    if (apiError.code === 'InvalidHarvestTotalQuantity') {
      return 'Quantidade total de créditos da emissão é inválida (zero ou nula).';
    }

    if (apiError.code === 'NotAuthorized') {
      return 'Usuário sem permissão ou sem conta vinculada para registrar o crédito de carbono.';
    }

    if (apiError.code === 'UnmappedError' && (apiError.details || apiError.message || '').includes('BigInteger')) {
      return 'Bug conhecido no issuer remoto (IdentityGenerator). Rode a API local com o fix e use npm run start:local.';
    }

    if (apiError.code === 'UnmappedError') {
      return `Erro interno na API: ${apiError.details || apiError.message || 'sem detalhes'}.`;
    }

    const readableMessage = apiError.details || apiError.message;
    if (readableMessage) {
      return apiError.code
        ? `[${apiError.code}] ${readableMessage}`
        : readableMessage;
    }

    if (apiError.status === 500) {
      return 'Erro interno ao registrar o crédito. Use npm run start:local com a API issuer rodando localmente, ou aguarde deploy do fix no DEV.';
    }

    return defaultMessage;
  }

  private flattenData(emissions: PsaProjectEmission[]): Record<string, unknown>[] {
    const flattened: Record<string, unknown>[] = [];

    emissions.forEach(emission => {
      (emission.harvests ?? []).forEach(harvest => {
        flattened.push({
          ID_DO_PROJETO: emission.project?.id,
          NOME_DO_PROJETO: emission.project?.name || '',
          ID_DA_EMISSAO: emission.id,
          SERIAL_CODE_EMISSAO: emission.serialCode,
          STATUS_DA_EMISSAO: emission.status,
          MOTIVO_REPROVACAO: emission.reasonRejection,
          DATE_DE_REGISTRO: emission.insertDate,
          NUMERO_EMISSAO: emission.emissionNumber,
          QUANTIDADE_TOTAL_EMISSAO: emission.emissionQuantity,
          DATA_INICIO: emission.startDate,
          DATA_FIM: emission.endDate,
          CNPJ_DESENVOLVEDOR: emission.cpfCnpjCode,
          CONTA_DETENTOR: emission.holderCode,
          ID_SAFRA: harvest.id,
          ANO_SAFRA: harvest.harvestYear,
          QUANTIDADE_SAFRA: harvest.quantity,
          NOME_DESENVOLVEDOR: harvest.developerName,
          ID_DESENVOLVEDOR: harvest.developerId,
          LINK_PARA_RM: emission.rmLink,
          DETALHES: emission.details,
        });
      });
    });

    return flattened;
  }

  private formatCSVField(field: unknown): string {
    if (field === null || field === undefined) {
      return '';
    }

    const fieldString = field.toString().replace(/"/g, '""');
    if (fieldString.search(/("|,|\n)/g) >= 0) {
      return `"${fieldString}"`;
    }

    return fieldString;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  private resetState(): void {
    this.loadSubscription?.unsubscribe();
    this.emission = undefined;
    this.harvests = [];
    this.isLoading = false;
    this.loadError = null;
    this.dismissAllModals();
    this.releaseOpenDialogs();
    this.updateViewModel();
  }

  private dismissAllModals(): void {
    this.showRejectModal = false;
    this.showApproveModal = false;
    this.showCloneModal = false;
  }

  private releaseOpenDialogs(): void {
    document.querySelectorAll('dialog[open]').forEach(element => {
      if (element instanceof HTMLDialogElement) {
        element.close();
      }
    });
  }
}
